import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Zap, FileText, Settings, Wallet, Building2, ArrowRight } from 'lucide-react';

const OffreOSCOP = () => {
  const creditPacks = [
    { credits: 10, price: 6, unit: 0.60 },
    { credits: 25, price: 14, unit: 0.56 },
    { credits: 50, price: 25, unit: 0.50 },
    { credits: 100, price: 45, unit: 0.45 },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 50%, #f5f3ff 100%)' }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-blue-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl text-blue-800">O'SCOP</span>
          </div>
          <nav className="flex items-center gap-6">
            <Link to="/offre-coppam" className="text-slate-600 hover:text-blue-600 transition">COPPAM</Link>
            <Link to="/offre-fogedom" className="text-slate-600 hover:text-blue-600 transition">FOGEDOM</Link>
            <Link to="/login" className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-medium hover:bg-blue-700 transition">
              Connexion
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            Production régulière de dossiers
          </span>
          <h1 className="text-5xl font-bold text-slate-800 mb-4">
            Offre Professionnel <span className="text-blue-600">O'SCOP</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Pour les structures produisant régulièrement des dossiers et avenants COPPAM.
            PME, coopératives, bureaux d'études.
          </p>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Main Plan Card */}
          <div className="bg-white rounded-3xl shadow-2xl shadow-blue-200/50 overflow-hidden">
            {/* Price Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8">
              <div className="flex items-center gap-2 text-blue-100 mb-2">
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">Recommandé</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-6xl font-bold text-white">79</span>
                <span className="text-2xl text-blue-100">€ /mois</span>
              </div>
              <div className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-full">
                <Wallet className="w-5 h-5 text-white" />
                <span className="text-white font-medium">120 crédits inclus / mois</span>
              </div>
              <p className="text-blue-100 text-sm mt-3">Soit 0,66 € / crédit inclus</p>
            </div>

            {/* Features */}
            <div className="p-8">
              <h3 className="font-semibold text-slate-800 mb-4">Tout ce dont vous avez besoin :</h3>
              <ul className="space-y-3">
                {[
                  'Simulations standards & spéciales',
                  'Avenants et personnalisations',
                  'Ajustement CA, durée, remises',
                  'Exports PDF / HTML',
                  'GED intégrée avec historique',
                  'Support prioritaire',
                  'Multi-utilisateurs'
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-blue-600" />
                    </div>
                    <span className="text-slate-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link 
                to="/inscription-ess?plan=professionnel"
                className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-2xl font-semibold text-lg flex items-center justify-center gap-2 transition-all hover:shadow-lg hover:shadow-blue-300/50"
              >
                Activer l'espace professionnel
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Credit Packs Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Settings className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-bold text-xl text-slate-800">Crédits supplémentaires</h3>
                <p className="text-slate-500 text-sm">Coût marginal dégressif</p>
              </div>
            </div>

            <div className="space-y-3">
              {creditPacks.map((pack, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-blue-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="font-bold text-blue-600">{pack.credits}</span>
                    </div>
                    <span className="text-slate-700 font-medium">{pack.credits} crédits</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-slate-800">{pack.price} €</div>
                    <div className="text-xs text-slate-500">{pack.unit.toFixed(2)} € / crédit</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Usage typical */}
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
              <h4 className="font-semibold text-slate-800 text-sm mb-2">Usage typique mensuel</h4>
              <p className="text-slate-600 text-sm">
                40–60 simulations + personnalisations + avenants
              </p>
            </div>
          </div>
        </div>

        {/* Barème */}
        <div className="max-w-3xl mx-auto mt-12 bg-white rounded-2xl p-6 shadow-lg">
          <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Barème de consommation
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { action: 'Simulation standard', credits: 2 },
              { action: 'Simulation spéciale', credits: 3 },
              { action: 'Avenant', credits: 5 },
              { action: 'Personnalisation', credits: 4 },
              { action: 'Génération PDF', credits: 2 },
              { action: 'Génération HTML', credits: 1 },
              { action: 'Duplication', credits: 1 },
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{item.credits}</div>
                <div className="text-xs text-slate-600">{item.action}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Legal Note */}
        <div className="text-center mt-8">
          <p className="text-sm text-slate-500 max-w-3xl mx-auto">
            <strong>Mention légale :</strong> O'SCOP fournit un service numérique d'ingénierie coopérative.
            Aucun transfert de risque – aucune police d'assurance – aucune activité réglementée.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OffreOSCOP;
